import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { Checkbox } from "@wix/wix-ui-tpa/cssVars";
import classNames from "classnames";
import { useRef, useMemo, useCallback } from "react";
import { useConfig } from "../../contexts/config-context.js";
import { useFocusFieldEvent } from "../../contexts/events-context.js";
import { useForcedState, ForcedState } from "../../contexts/forced-state-context.js";
import { useTranslation } from "../../contexts/translation-context.js";
import { SelectMode } from "../../field-settings/settings-shared/options/options-list/types.js";
import { getSelectMode } from "../../fields/product-list/product-list-settings/select-mode-setting/get-select-mode.js";
import { useFieldAttributes } from "../../services/field-attributes.js";
import { FieldDescriptionWUT } from "../field-description/field-description-wut.js";
import { FieldErrorWUT } from "../field-error/field-error-wut.js";
import { FieldLabelWUT } from "../field-label/field-label-wut.js";
import { getCurrencyAriaLabel } from "../shared/payment-field-utils.js";
import { Widget } from "../widget/widget.js";
import { ProductWUT } from "./product/product-wut.js";
import { classes } from "./product-list-wut.st.css";
import styles from "./product-list.module.scss";
import { Fieldset } from "../fieldset/fieldset.js";
const ProductListComponentWUT = (props) => {
  const {
    id,
    target,
    description,
    required,
    hideLabel,
    label,
    options,
    value,
    error,
    hasError = false,
    errorMessage,
    onChange,
    onFocus,
    onBlur,
    imageResize,
    FieldLayout,
    validation,
    requiredIndicatorType,
    requiredIndicatorPlacement,
    widgetHtml,
    widgetEnabled
  } = props;
  const { labelId, inputId, errorId, descriptionId, ariaDescribedBy } = useFieldAttributes(id, error, description);
  const { currency } = useConfig();
  const { t } = useTranslation();
  const firstCheckboxRef = useRef(null);
  const forcedHover = useForcedState().includes(ForcedState.INPUT_FIELDS_HOVER);
  const selectOptionsMode = getSelectMode(
    validation?.predefined?.paymentOptions
  );
  const allowMany = selectOptionsMode === SelectMode.many;
  const _value = useMemo(() => value || [], [value]);
  const _onChange = useCallback(
    (newValue) => {
      onChange?.(newValue);
    },
    [onChange]
  );
  const handleChange = useCallback(
    (productValue, isChecked) => {
      const restProductValues = _value.filter(
        (val) => val.productId !== productValue.productId
      );
      const newProductValues = isChecked ? [...restProductValues, productValue] : restProductValues;
      _onChange(newProductValues);
    },
    [_value, _onChange]
  );
  useFocusFieldEvent(() => {
    firstCheckboxRef.current?.focus({ preventScroll: true });
  }, id);
  return /* @__PURE__ */ jsx(
    FieldLayout,
    {
      fieldId: id,
      renderLabel: () => /* @__PURE__ */ jsx(
        FieldLabelWUT,
        {
          labelId,
          required,
          label,
          hideLabel,
          requiredIndicatorType,
          requiredIndicatorPlacement
        }
      ),
      renderInput: () => widgetHtml && widgetEnabled ? /* @__PURE__ */ jsx(
        Widget,
        {
          fieldId: id,
          widgetHtml,
          onChange,
          value
        }
      ) : /* @__PURE__ */ jsx(
        Fieldset,
        {
          id: inputId,
          ariaLabelledBy: labelId,
          className: classNames(styles.root),
          ariaDescribedBy,
          onFocus,
          onBlur,
          required,
          role: "group",
          children: options?.map((option, index) => {
            const price = validation?.predefined?.paymentOptions?.products?.find(
              (p) => p.id === option.value
            )?.fixedPriceOptions?.price || "0";
            const currencyTranslation = getCurrencyAriaLabel(t, currency);
            const preselectedProduct = _value.find(
              (i) => i.productId === option.value
            );
            return allowMany ? /* @__PURE__ */ jsx(
              ProductWUT,
              {
                hasError,
                item: option,
                price,
                quantity: preselectedProduct?.quantity,
                imageResize,
                onQuantityChange: (quantity) => {
                  handleChange(
                    {
                      productId: option.value,
                      price,
                      quantity
                    },
                    Boolean(quantity)
                  );
                }
              }
            ) : /* @__PURE__ */ jsx(
              Checkbox,
              {
                checked: Boolean(preselectedProduct),
                className: classNames(classes.checkbox, styles.checkbox, {
                  [styles.checkboxError]: hasError,
                  [classes.hover]: forcedHover
                }),
                "aria-label": `${option.label} ${currencyTranslation}`,
                label: /* @__PURE__ */ jsx(
                  ProductWUT,
                  {
                    item: option,
                    price,
                    imageResize
                  }
                ),
                onChange: (event) => {
                  handleChange(
                    {
                      productId: option.value,
                      price,
                      quantity: 1
                    },
                    event.checked
                  );
                },
                ref: index === 0 ? firstCheckboxRef : void 0
              }
            );
          })
        }
      ),
      renderDescription: () => /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsx(
          FieldErrorWUT,
          {
            id: errorId,
            target,
            hasError,
            errorMessage
          }
        ),
        /* @__PURE__ */ jsx(FieldDescriptionWUT, { description, id: descriptionId })
      ] })
    }
  );
};
export {
  ProductListComponentWUT
};
//# sourceMappingURL=product-list-wut.js.map
